/**
 * Goals Page
 *
 * Main page for goal management with tabs and filtering.
 * Goals represent desired outcomes (distinct from values which are about being).
 */

import { useState } from 'react';
import { Target, TrendingUp, CheckCircle, Plus, TreeDeciduous } from 'lucide-react';
import { useGoals, useGoalStats, useGoalTree, useGoalMutations } from '@/hooks/use-goals';
import { useTaskMutations } from '@/hooks/use-tasks';
import { GoalList, GoalFiltersBar, GoalEditModal, GoalCreateModal } from '@/components/goals';
import { TaskEditModal } from '@/components/tasks/task-edit-modal';
import { Card, CardContent, Button } from '@/components/ui';
import type { Goal, GoalFilters, GoalGroupBy, UpdateGoalInput, CreateGoalInput, Task, UpdateTaskInput } from '@/types';

type TabType = 'all' | 'active' | 'completed' | 'tree';

export function GoalsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [filters, setFilters] = useState<GoalFilters>({});
  const [groupBy, setGroupBy] = useState<GoalGroupBy | undefined>(undefined);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  // Fetch goals based on active tab
  const allGoalsQuery = useGoals(filters);
  const activeGoalsQuery = useGoals({ ...filters, status: 'active' });
  const completedGoalsQuery = useGoals({ ...filters, status: 'completed' });
  const treeQuery = useGoalTree();
  const statsQuery = useGoalStats();
  const { createGoal, updateGoal, completeGoal, reopenGoal, deleteGoal } = useGoalMutations();
  const { updateTask, completeTask, dismissTask, convertToReference } = useTaskMutations();

  // Handle create goal
  const handleCreateGoal = (input: CreateGoalInput) => {
    createGoal.mutate(input, {
      onSuccess: () => {
        setShowCreateModal(false);
      },
    });
  };

  // Handle edit goal
  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
  };

  const handleSaveGoal = (goalId: string, updates: UpdateGoalInput) => {
    updateGoal.mutate(
      { goalId, input: updates },
      {
        onSuccess: () => {
          setEditingGoal(null);
        },
      }
    );
  };

  // Handle complete/reopen goal
  const handleCompleteGoal = (goalId: string) => {
    completeGoal.mutate(goalId);
  };

  const handleReopenGoal = (goalId: string) => {
    reopenGoal.mutate(goalId);
  };

  const handleDeleteGoal = (goalId: string) => {
    deleteGoal.mutate(goalId);
  };

  // Handle task editing from goal modal
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  const handleSaveTask = (taskId: string, updates: UpdateTaskInput) => {
    updateTask.mutate(
      { taskId, input: updates },
      {
        onSuccess: () => {
          setEditingTask(null);
        },
      }
    );
  };

  const handleCompleteTask = (taskId: string) => {
    completeTask.mutate(taskId);
    setEditingTask(null);
  };

  const handleDismissTask = (taskId: string) => {
    dismissTask.mutate(taskId);
    setEditingTask(null);
  };

  const handleConvertToReference = (taskId: string) => {
    convertToReference.mutate(taskId);
    setEditingTask(null);
  };

  // Get the right data based on active tab
  const getActiveData = () => {
    switch (activeTab) {
      case 'active':
        return {
          goals: activeGoalsQuery.data?.goals ?? [],
          isLoading: activeGoalsQuery.isLoading,
          count: activeGoalsQuery.data?.total ?? 0,
        };
      case 'completed':
        return {
          goals: completedGoalsQuery.data?.goals ?? [],
          isLoading: completedGoalsQuery.isLoading,
          count: completedGoalsQuery.data?.total ?? 0,
        };
      case 'tree':
        // Flatten tree for display with children
        return {
          goals: treeQuery.data?.tree ?? [],
          isLoading: treeQuery.isLoading,
          count: treeQuery.data?.count ?? 0,
        };
      default:
        return {
          goals: allGoalsQuery.data?.goals ?? [],
          isLoading: allGoalsQuery.isLoading,
          count: allGoalsQuery.data?.total ?? 0,
        };
    }
  };

  const activeData = getActiveData();
  const stats = statsQuery.data;
  
  // Filter out completed goals if showCompleted is false (only for 'all' tab)
  const filteredGoals = activeTab === 'all' && !showCompleted
    ? activeData.goals.filter(g => g.status !== 'completed')
    : activeData.goals;
  
  const goals = filteredGoals;
  const isLoading = activeData.isLoading;
  const count = filteredGoals.length;

  const tabs = [
    { id: 'active' as const, label: 'Active', icon: Target, count: stats?.by_status?.active },
    { id: 'all' as const, label: 'All Goals', icon: TrendingUp },
    { id: 'completed' as const, label: 'Completed', icon: CheckCircle, count: stats?.by_status?.completed },
    { id: 'tree' as const, label: 'Tree View', icon: TreeDeciduous },
  ];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goals</h1>
          <p className="text-gray-600 mt-1">
            Track your desired outcomes and progress
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Goal
        </Button>
      </div>

      {/* Conceptual distinction callout */}
      <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <Target className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Goals vs Values</h3>
            <p className="text-sm text-gray-600 mt-1">
              <strong>Goals</strong> are specific outcomes you want to achieve (what you do). 
              <strong className="ml-1">Values</strong> are about who you want to be (how you live). 
              Goals can be completed; values are ongoing.
            </p>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_goals}</p>
                <p className="text-sm text-gray-500">Total Goals</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_status?.active ?? 0}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_status?.completed ?? 0}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <div className="w-5 h-5 flex items-center justify-center">
                  <span className="text-lg font-bold text-purple-600">{Math.round(stats.average_progress)}%</span>
                </div>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{Math.round(stats.average_progress)}%</p>
                <p className="text-sm text-gray-500">Avg Progress</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters - only show on All tab */}
      {activeTab === 'all' && (
        <div className="mb-4">
          <GoalFiltersBar
            filters={filters}
            onFiltersChange={setFilters}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            showCompleted={showCompleted}
            onShowCompletedChange={setShowCompleted}
          />
        </div>
      )}

      {/* Results count */}
      <p className="text-sm text-gray-500 mb-4">
        {count} goal{count !== 1 ? 's' : ''}
        {activeTab === 'active' && ' currently active'}
        {activeTab === 'completed' && ' completed'}
      </p>

      {/* Goal list */}
      <Card>
        <CardContent className="p-4">
          <GoalList
            goals={goals}
            isLoading={isLoading}
            groupBy={activeTab === 'all' ? groupBy : undefined}
            onEdit={handleEditGoal}
            onComplete={handleCompleteGoal}
            onReopen={handleReopenGoal}
            onDelete={handleDeleteGoal}
            onClick={handleEditGoal}
            emptyMessage={
              activeTab === 'active'
                ? 'No active goals. Create one to start tracking!'
                : activeTab === 'completed'
                ? 'No completed goals yet. Keep working toward your goals!'
                : 'No goals found'
            }
          />
        </CardContent>
      </Card>

      {/* Create Goal Modal */}
      <GoalCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateGoal}
        isCreating={createGoal.isPending}
      />

      {/* Edit Goal Modal */}
      {editingGoal && (
        <GoalEditModal
          goal={editingGoal}
          isOpen={!!editingGoal}
          onClose={() => setEditingGoal(null)}
          onSave={handleSaveGoal}
          isSaving={updateGoal.isPending}
          onComplete={(goalId) => {
            handleCompleteGoal(goalId);
            setEditingGoal(null);
          }}
          onDelete={(goalId) => {
            handleDeleteGoal(goalId);
            setEditingGoal(null);
          }}
          onEditTask={handleEditTask}
        />
      )}

      {/* Edit Task Modal (opened from goal's supporting tasks) */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          onSave={handleSaveTask}
          isSaving={updateTask.isPending}
          onComplete={handleCompleteTask}
          onDismiss={handleDismissTask}
          onConvertToReference={handleConvertToReference}
        />
      )}
    </div>
  );
}

